// On-field peasant cats — one mesh per owned goon (up to VisualGoonCap).
// Listens to GoonOwnedChange and spawns/despawns voxel cats accordingly.
// Each peasant wanders to random tile positions on a perpetual loop.

import * as THREE from 'three'
import gsap from 'gsap'
import { on, emit, Events } from '../ui/eventBus.js'
import { Field, GoonTypes, VisualGoonCap, AssetKeys, HungerConfig, BuildingTypes, SchoolConfig, WorkModes, PlotTypes, HospitalConfig, PositiveToolConfig, RestConfig, CollisionConfig, FieldWorkConfig, FeralConfig, DirectTendConfig } from '../config/balance.js'
import { fieldRadiusWorld, GROUND_Y, randomFieldPoint, isInFieldWorld } from './hexGrid.js'
import { isBlocked } from './collision.js'

const FIELD_HALF = fieldRadiusWorld()
import * as Assets from '../assets/AssetRegistry.js'
import * as season from '../game/seasonManager.js'
import { getBuildings } from '../game/buildingState.js'
import { tileToWorld, listAllTiles, getHomeTileIndex } from '../game/plotState.js'
import { isResting } from '../game/restManager.js'
import { splatterBurst } from './effects.js'
import { createSyntyChar, syntyCharsReady, scaleOf, playClip, clipDuration } from './syntyChars.js'
import { groundLift, getPaddyFields } from './paddyRows.js'
import { createRapidClicks, pickCrew, nextField, planLegs } from '../game/fieldWork.js'
import * as hospital from '../game/hospital.js'
import { stageAtLeast } from '../game/hungerRules.js'

// Loyal palette — warm/friendly colors with neutral black eyes. Each goon
// type has its own thematic look so you can read class at a glance, and
// none of them get the red glowing eyes that mark a wild goon as antagonist.
const ColorByGoon = {
  // Peasant — sandy farm cat
  [GoonTypes.PEASANT_CAT]: { body: 0xa0826d, head: 0x806652, ear: 0x5c4838 },
  // Scribe — scholarly blue
  [GoonTypes.SCRIBE_CAT]: { body: 0x6b94c8, head: 0x4a7099, ear: 0x335276 },
  // Noble — regal cream-and-gold
  [GoonTypes.NOBLE_CAT]: { body: 0xe8d8a0, head: 0xc8b070, ear: 0x9b8a50 },
}

const EYE_COLOR = 0x1a1a1a // neutral black — wild goons get the red glow

const AssetKeyByGoon = {
  [GoonTypes.PEASANT_CAT]: AssetKeys.PEASANT_CAT,
  [GoonTypes.SCRIBE_CAT]: AssetKeys.SCRIBE_CAT,
  [GoonTypes.NOBLE_CAT]: AssetKeys.NOBLE_CAT,
}

const meshes = new Map() // goonId → THREE.Mesh[]

// All visible citizen meshes (for the positive tool's raycast + tests).
export function getPeasantObjects() {
  const out = []
  for (const list of meshes.values()) for (const p of list) if (!p.userData.inside) out.push(p) // (not those asleep indoors)
  return out
}
const hopCursors = new Map() // goonId → next index for round-robin hop
let sceneRef = null
let heroRef = null
let fxRef = null
let riceRef = null

// Minimum spacing between any two cats (or cat ↔ hero). Originally 0.7 to
// match scale-1.0 cats taking ~half a tile each; with cats now at
// Field.catScale = 0.1625 their bodies are ~0.11 wide, so 0.18 keeps a small
// breathing buffer without making them ghost through each other.
const SEPARATION_DIST = 0.28 // Synty villagers are ~2× the old cats' footprint

export function createPeasants({ scene, hero, fx = null, rice = null }) { // fx: scene/sparkles.js · rice: scene/riceField.js (the tending feedback)
  if (import.meta.env?.DEV) window.__peasants = { all: () => [...meshes.values()].flat(), freeze: () => [...meshes.values()].flat().forEach((m) => gsap.killTweensOf(m.position)), realTime: () => gsap.ticker.lagSmoothing(0), restless: () => [...meshes.values()].flat().map((m, i) => (m.userData.restless ? { i, goonId: m.userData.goonId, sinceMs: Date.now() - m.userData.restless.since, mark: !!m.userData.restlessMark } : null)).filter(Boolean), markRestless: () => { const id = markRestless(); if (!id) return null; const all = [...meshes.values()].flat(); let best = -1; all.forEach((m, i) => { if (m.userData.restless && (best < 0 || m.userData.restless.since > all[best].userData.restless.since)) best = i }); return { i: best, goonId: id } }, warpRestless: (ms, i = null) => { [...meshes.values()].flat().forEach((m, k) => { if (m.userData.restless && (i == null || k === i)) m.userData.restless.since -= ms }) }, educate: (i) => educatePeasant([...meshes.values()].flat()[i]) } // dev/test hook (realTime: a slow software renderer must not slow animations down)
  if (import.meta.env?.DEV) window.__crew = { // dev/test hook: the clicking-crew
    rapid: () => fieldClicks.rapid(performance.now()),
    fields: () => getPaddyFields(FieldWorkConfig.rowEndMarginM),
    workers: () => crewWorkers().map((p) => ({ goonId: p.userData.goonId, slot: p.userData.fieldJob.slot, fieldId: p.userData.fieldJob.fieldId, i: p.userData.fieldJob.i, n: p.userData.fieldJob.legs.length, x: p.position.x, z: p.position.z, evading: !!p.userData.evading, working: p.userData.working ?? null })),
    play: (i, name, opts = {}) => playClip([...meshes.values()].flat()[i], name, opts), // dev: play a clip on citizen i
    tendStats: () => JSON.parse(JSON.stringify(tendStats)), direct: (i, opts) => directCitizen([...meshes.values()].flat()[i], opts), directed: () => [...meshes.values()].flat().map((m, i) => (m.userData.directed ? { i, k: m.userData.directed.k, pay: m.userData.directed.pay } : null)).filter(Boolean), setWorkAnims, clips: () => { const m = [...meshes.values()].flat().find((x) => x.userData.synty); return m ? Object.keys(m.userData.synty.actions) : [] },
  }
  sceneRef = scene
  heroRef = hero ?? null
  fxRef = fx; riceRef = rice
  on('paw:click', () => fieldClicks.click(performance.now())) // the player's own press on the clicker (not the citizens' automatic clicks)
  for (const id of Object.values(GoonTypes)) {
    meshes.set(id, [])
    hopCursors.set(id, 0)
  }
  on(Events.GoonOwnedChange, ({ goonId, owned }) => sync(goonId, owned))
  on(Events.GoonClick, ({ goonId }) => pickAndHop(goonId))
  on(Events.RowFilled, ({ k }) => chainToRow(k)) // the row chain
  on(Events.CatHungerChange, ({ state, stage }) => applyHungerToAll(state, stage))
  on(Events.CatStarvationDeath, () => starveOneRandom()) // (no longer emitted — starving citizens are admitted to the Hospital instead)
  on(Events.CitizenAdmitted, ({ goonId }) => admitToHospital(goonId))
  on(Events.PatientsHealed, () => releasePatients())
  on(Events.HospitalChange, () => syncPatients())
  on(Events.FeedActivated, healAllInjured) // a meal mends goon-fight injuries
  // Cache building + rice-paddy positions: schools educate passersby, hospitals
  // heal injured passersby, and FIELDS-mode workers migrate to their nearest paddy.
  refreshBuildingPositions()
  refreshFarmPositions()
  on(Events.BuildingAdded, refreshBuildingPositions)
  on(Events.KingdomChange, () => { refreshBuildingPositions(); refreshFarmPositions(); resyncAll(); applyRest(false); syncPatients() })
  on(Events.PlotChanged, () => { refreshBuildingPositions(); refreshFarmPositions() })
  // Switching Fields ⇄ Village redeploys the workers right away.
  on(Events.WorkModeChange, retargetAllForMode)
  // Spawn the citizens the player ALREADY owns. Without this, sync() only ran on
  // GoonOwnedChange (a buy), so a returning player's saved cats never appeared on
  // the field — leaving nothing to hover/tend. Re-sync when the cloud state lands.
  on(Events.AccountStatePulled, () => { resyncAll(); applyRest(false); syncPatients() })
  // REST sends everyone to the nearest building; WORK (or the timer) brings them back out.
  on(Events.RestChange, () => applyRest(true))
  resyncAll()
  syncPatients()
}

// Spawn/despawn visual cats so the field matches the active kingdom's owned counts.
function resyncAll() {
  for (const id of Object.values(GoonTypes)) sync(id, season.getGoonsOwned(id))
}

let farmPositions = []
function refreshFarmPositions() {
  farmPositions = listAllTiles()
    .filter((t) => t.plot?.type === PlotTypes.FARM)
    .map((t) => tileToWorld(t.tileIndex))
}

// Re-pick every (non-busy) citizen's wander target so they visibly redeploy
// to the fields or back to the village the moment the work mode changes.
function retargetAllForMode() {
  for (const list of meshes.values()) {
    for (const peasant of list) {
      if (peasant.userData.dying || peasant.userData.evading || peasant.userData.frozen || peasant.userData.resting) continue
      gsap.killTweensOf(peasant.position, 'x,z')
      wander(peasant)
    }
  }
}

let schoolPositions = []
let hospitalPositions = []
function buildingPositions(type) {
  return getBuildings().filter((b) => b.type === type).map((b) => tileToWorld(b.tileIndex))
}
function refreshBuildingPositions() {
  schoolPositions = buildingPositions(BuildingTypes.SCHOOL)
  hospitalPositions = buildingPositions(BuildingTypes.HOSPITAL)
}

// Small dark "mortarboard" cap on a graduated citizen's head (local cat space).
function addMortarboard(cat) {
  // Only Scribes wear the graduation cap. Peasant farmers keep their rice hats (their education still counts; it just isn't worn).
  if (cat.userData.goonId !== GoonTypes.SCRIBE_CAT) return
  if (cat.userData.cap) return
  const board = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.06, 0.55),
    new THREE.MeshLambertMaterial({ color: 0x20202c }),
  )
  const ui = cat.userData.ui
  board.position.set(0, ui?.capY ?? 1.12, 0)
  if (ui) board.scale.setScalar(ui.capScale)
  cat.add(board)
  cat.userData.cap = board
}

let currentHungerState = 'healthy'
let currentHungerStage = 'healthy'

function applyHungerToAll(state, stage = null) {
  currentHungerState = state
  if (stage) currentHungerStage = stage
  for (const list of meshes.values()) {
    for (const peasant of list) { applyHungerVisual(peasant, state); applyHungerMark(peasant, currentHungerStage) }
  }
}

// ── Hunger marks over the head: a yellow ⚠ from the 'caution' stage, a red cross from 'critical' (hungerRules.js) ──
const CAUTION_MAT = new THREE.MeshBasicMaterial({ color: 0xffd23d })
const CAUTION_DARK = new THREE.MeshBasicMaterial({ color: 0x3a2a00 })
function buildCautionMark() {
  const g = new THREE.Group()
  const tri = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.05, 3), CAUTION_MAT) // a flat triangular prism = the warning triangle
  tri.rotation.x = Math.PI / 2
  tri.rotation.z = Math.PI // point up
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.12, 0.06), CAUTION_DARK) // the "!"
  bar.position.set(0, 0.02, 0)
  const dot = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.06), CAUTION_DARK)
  dot.position.set(0, -0.09, 0)
  g.add(tri, bar, dot)
  return g
}
function buildCrossMark() {
  const g = new THREE.Group()
  g.add(new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.24, 0.07), INJURY_MAT))
  g.add(new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.07, 0.07), INJURY_MAT))
  return g
}
function applyHungerMark(cat, stage) {
  const want = stageAtLeast(stage, 'critical') ? 'cross' : stageAtLeast(stage, 'caution') ? 'caution' : null
  const have = cat.userData.hungerMarkKind ?? null
  if (want === have) return
  if (cat.userData.hungerMark) { cat.remove(cat.userData.hungerMark); cat.userData.hungerMark = null }
  cat.userData.hungerMarkKind = want
  if (!want) return
  const mark = want === 'cross' ? buildCrossMark() : buildCautionMark()
  const ui = cat.userData.ui
  mark.position.set(0, (ui?.markY ?? 1.32) + (cat.userData.injuryMark ? 0.3 : 0), 0) // above an injury cross, if it wears one
  if (ui) mark.scale.setScalar(ui.markScale)
  cat.add(mark)
  cat.userData.hungerMark = mark
}

function applyHungerVisual(cat, state) {
  // Injured cats stay skinny regardless of the global hunger state.
  const skinny = cat.userData.injured || state === 'skinny' || state === 'dying'
  const s = skinny ? HungerConfig.skinnyBodyXZ : 1
  cat.traverse((obj) => {
    if (obj.userData?.role === 'body') obj.scale.set(s, 1, s)
  })
}

// ── Injury ────────────────────────────────────────────────────────────────
// A citizen that brawls a goon survives (the goon dies) but drops to skinny +
// gets a red medical cross, and earns nothing until healed (Feed, a Hospital or a Devout cat).
const INJURY_MAT = new THREE.MeshBasicMaterial({ color: 0xff3030 })

export function injurePeasant(cat) {
  if (!cat || cat.userData.dying || cat.userData.injured) return
  cat.userData.injured = true
  applyHungerVisual(cat, currentHungerState) // → skinny
  if (!cat.userData.injuryMark) {
    const mark = new THREE.Group()
    mark.add(new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.24, 0.07), INJURY_MAT))
    mark.add(new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.07, 0.07), INJURY_MAT))
    const ui = cat.userData.ui
    mark.position.set(0, ui?.markY ?? 1.32, 0)
    if (ui) mark.scale.setScalar(ui.markScale)
    cat.add(mark)
    cat.userData.injuryMark = mark
  }
  season.injureCitizen()
}

function restoreHealth(cat) {
  if (!cat.userData.injured) return false
  cat.userData.injured = false
  applyHungerVisual(cat, currentHungerState) // back to normal (unless globally hungry)
  if (cat.userData.injuryMark) {
    cat.remove(cat.userData.injuryMark)
    cat.userData.injuryMark = null
  }
  return true
}

// Feed heals everyone at once (a hearty meal mends the wounded).
function healAllInjured() {
  for (const list of meshes.values()) {
    for (const cat of list) restoreHealth(cat)
  }
  season.healAllInjuries()
}

// ── Positive tool helpers (key 2) ───────────────────────────────────────────
// Freeze a citizen in place (kill its wander tween) so the player can act on
// it via the popup menu; unfreeze resumes wandering.
export function freezePeasant(cat) {
  if (!cat || cat.userData.frozen || cat.userData.dying) return
  cat.userData.frozen = true
  cat.userData.pauseCall?.kill(); cat.userData.pauseCall = null
  cat.userData.evading = false // (a bump in progress is cancelled; unfreezing starts it wandering again)
  gsap.killTweensOf(cat.position, 'x,z')
}
export function unfreezePeasant(cat) {
  if (!cat || !cat.userData.frozen) return
  cat.userData.frozen = false
  if (!cat.userData.dying) wander(cat)
}

// Graduate a citizen on the spot — mortarboard + counts toward the educated
// bonus (also makes it immune to turning feral). Returns false if already
// educated or the educated cap is full.
export function educatePeasant(cat) {
  if (!cat || cat.userData.educated || cat.userData.dying) return false
  if (!season.educateCitizen()) return false
  cat.userData.educated = true
  addMortarboard(cat)
  clearRestless(cat) // schooled in time: it settles
  return true
}

// Heal an injured citizen instantly (the Hospital walk-by, on demand).
export function healPeasant(cat) {
  if (!cat || !cat.userData.injured || cat.userData.dying) return false
  if (!restoreHealth(cat)) return false
  season.healCitizen()
  splatterBurst({ scene: sceneRef, position: cat.position, colors: [0x66ff99, 0xbfffd6, 0xffffff], count: 8 })
  return true
}

// Bless a citizen at the Temple → Devout: a golden halo, immune to turning
// feral (the feral shield reads userData.devout), and it mends any injured cat
// it brushes past (see tickPeasants). Returns false if already devout.
const HALO_MAT = new THREE.MeshBasicMaterial({ color: 0xffe08a })
export function blessPeasant(cat) {
  if (!cat || cat.userData.devout || cat.userData.dying) return false
  cat.userData.devout = true
  clearRestless(cat) // blessed in time: it settles
  if (!cat.userData.halo) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.05, 8, 20), HALO_MAT)
    ring.rotation.x = Math.PI / 2
    const ui = cat.userData.ui
    ring.position.set(0, ui?.haloY ?? 1.5, 0)
    if (ui) ring.scale.setScalar(ui.haloScale)
    cat.add(ring)
    cat.userData.halo = ring
  }
  splatterBurst({ scene: sceneRef, position: cat.position, colors: [0xffd84d, 0xfff3c4, 0xffffff], count: 12 })
  return true
}

// Gun-mode friendly-fire check — raycasts against every visible loyal
// peasant + the hero. Returns { type: 'hero' } if the player shot their own
// avatar (no death — just the shame). Returns { type: 'peasant', goonId,
// name } if they shot a loyal cat (kills the peasant via the existing death
// path so the death animation runs). heroController calls this after
// wildGoons.tryShoot misses, so a wild goon in the line of fire still takes
// the bullet first.
const _friendlyRaycaster = new THREE.Raycaster()

export function tryShootFriendly(camera, pointerNdc) {
  if (!sceneRef) return null
  const candidates = []
  const meshToOwner = new Map()
  for (const list of meshes.values()) {
    for (const peasant of list) {
      if (peasant.userData.dying || peasant.userData.inside) continue
      peasant.traverse((obj) => {
        if (obj.isMesh) { candidates.push(obj); meshToOwner.set(obj, peasant) }
      })
    }
  }
  if (heroRef) {
    heroRef.traverse((obj) => {
      if (obj.isMesh) { candidates.push(obj); meshToOwner.set(obj, heroRef) }
    })
  }
  if (!candidates.length) return null
  _friendlyRaycaster.setFromCamera(pointerNdc, camera)
  const hits = _friendlyRaycaster.intersectObjects(candidates, false)
  if (!hits.length) return null
  const owner = meshToOwner.get(hits[0].object)
  if (!owner) return null
  if (owner === heroRef) return { type: 'hero' }
  // Loyal peasant — kill it via the same death path the wild goons use, and
  // surface the goon type so the emperor can name what the player wasted.
  const goonId = owner.userData?.goonId
  killPeasant(owner, 'shot by the player')
  return { type: 'peasant', goonId }
}

function starveOneRandom() {
  const candidates = []
  for (const list of meshes.values()) {
    for (const peasant of list) if (!peasant.userData.dying) candidates.push(peasant)
  }
  if (!candidates.length) return
  killPeasant(candidates[Math.floor(Math.random() * candidates.length)], 'starved (not fed)')
}

// Returns all loyal peasants within `radius` of `pos`. Used by wildGoons to
// find kill targets. Excludes already-dying meshes so the same victim isn't
// triggered twice during its death animation.
export function findPeasantsNear(pos, radius) {
  const hits = []
  for (const list of meshes.values()) {
    for (const peasant of list) {
      if (peasant.userData.dying || peasant.userData.inside) continue // citizens asleep indoors are safe from raiders
      const dx = peasant.position.x - pos.x
      const dz = peasant.position.z - pos.z
      if (Math.hypot(dx, dz) <= radius) hits.push(peasant)
    }
  }
  return hits
}

// Death sequence — every cat death (peasant / scribe / noble) explodes
// into the shared splatter burst with that cat's own color palette.
// Owner count decrements; the GoonOwnedChange that fires won't
// double-remove since we already pulled this mesh out of the logical list.
export function killPeasant(mesh, reason = 'killed') {
  const goonId = mesh.userData.goonId
  if (!goonId || mesh.userData.dying) return
  const list = meshes.get(goonId)
  if (!list) return
  const idx = list.indexOf(mesh)
  if (idx === -1) return

  mesh.userData.dying = true
  list.splice(idx, 1)
  gsap.killTweensOf(mesh.position)
  gsap.killTweensOf(mesh.rotation)
  gsap.killTweensOf(mesh.scale)

  // Splatter in the cat's own colors so a peasant pops sandy-brown, a
  // scribe pops scholarly blue, a noble pops cream/gold.
  const c = ColorByGoon[goonId] ?? ColorByGoon[GoonTypes.PEASANT_CAT]
  splatterBurst({
    scene: sceneRef,
    position: mesh.position,
    colors: [c.body, c.head, c.ear, EYE_COLOR],
  })
  sceneRef?.remove(mesh)

  // Decrementing fires GoonOwnedChange → sync(target=newCount). Since we
  // already removed `mesh` from `list`, sync sees list.length === newCount
  // and no extra peasant gets popped.
  season.adjustGoonsOwned(goonId, -1, reason)
}

// ── Feral turning (Step E) ──────────────────────────────────────────────────
// Pull one eligible citizen out of the roster to "turn feral": a neglected cat
// (NOT educated, NOT Temple-blessed/devout), uninjured, and not mid-dodge. The
// loyal mesh is removed with a red corruption burst and the owner count drops;
// the citizen's last position is returned so wildGoons can drop a hostile goon
// there. Returns null when nobody's eligible — i.e. the School/Temple shield
// covered everyone, so the village holds.
const FERAL_BURST = [0xb1271f, 0x7a1a14, 0x2a2a2a]
const feralEligible = (p) => { const u = p.userData; return !(u.dying || u.educated || u.devout || u.injured || u.evading || u.resting || u.frozen || u.patient) }

// ── RESTLESS: the warning before a feral turn ──────────────────────────────────────────────────────────────────────────
// The feral roll (wildGoons.js) no longer snatches a citizen outright: it marks one RESTLESS — a pulsing purple mark over its head,
// a banner and a log line — and only if the citizen is still unschooled and unblessed after FeralConfig.restlessMs does it turn.
// Schooling it (press 2, or a walk past the School) or blessing it at the Temple settles it at once.
const RESTLESS_MAT = new THREE.MeshBasicMaterial({ color: 0x9b5bff })
const RESTLESS_INK = new THREE.MeshBasicMaterial({ color: 0xf4ecff })
function buildRestlessMark() {
  const g = new THREE.Group()
  const gem = new THREE.Mesh(new THREE.OctahedronGeometry(0.17), RESTLESS_MAT)
  gem.scale.set(0.8, 1.25, 0.5)
  const bar = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.13, 0.08), RESTLESS_INK)
  bar.position.y = 0.03
  const dot = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.08), RESTLESS_INK)
  dot.position.y = -0.1
  g.add(gem, bar, dot)
  return g
}
function restlessCount() { let n = 0; for (const list of meshes.values()) for (const p of list) if (p.userData.restless) n++; return n }
export function markRestless(now = Date.now()) {
  if (restlessCount() >= FeralConfig.maxRestless) return null
  const pool = []
  for (const list of meshes.values()) for (const p of list) if (feralEligible(p) && !p.userData.restless) pool.push(p)
  if (!pool.length) return null
  const cat = pool[Math.floor(Math.random() * pool.length)]
  cat.userData.restless = { since: now }
  const mark = buildRestlessMark()
  const ui = cat.userData.ui
  mark.position.set(0, (ui?.markY ?? 1.32) + (cat.userData.hungerMark || cat.userData.injuryMark ? 0.34 : 0), 0) // above a hunger / injury mark
  if (ui) mark.scale.setScalar(ui.markScale)
  mark.userData.baseScale = mark.scale.x
  cat.add(mark)
  cat.userData.restlessMark = mark
  emit(Events.CitizenRestless, { goonId: cat.userData.goonId, ms: FeralConfig.restlessMs })
  return cat.userData.goonId
}
function clearRestless(cat) {
  const u = cat?.userData
  if (!u?.restless) return
  u.restless = null
  if (u.restlessMark) { cat.remove(u.restlessMark); u.restlessMark = null }
}
// The restless citizen whose time is up (still unschooled, unblessed, and out in the field): pulled out of the roster and its
// position returned so wildGoons can drop a hostile goon there. null when no one is due.
export function extractExpiredRestless(now = Date.now()) {
  for (const list of meshes.values()) {
    for (const p of list) {
      const u = p.userData
      if (!u.restless) continue
      if (u.educated || u.devout || u.dying) { clearRestless(p); continue } // settled (or gone) meanwhile
      if (now - u.restless.since < FeralConfig.restlessMs) continue
      if (!feralEligible(p)) continue // waits: asleep, held, in the Hospital…
      return extractFeralCandidate(p)
    }
  }
  return null
}

export function extractFeralCandidate(which = null) {
  let cat = which
  if (!cat) {
    const pool = []
    for (const list of meshes.values()) for (const p of list) if (feralEligible(p)) pool.push(p)
    if (!pool.length) return null
    cat = pool[Math.floor(Math.random() * pool.length)]
  }
  clearRestless(cat)
  const goonId = cat.userData.goonId
  const list = meshes.get(goonId)
  const idx = list ? list.indexOf(cat) : -1
  if (idx === -1) return null
  const pos = { x: cat.position.x, z: cat.position.z }

  cat.userData.dying = true
  list.splice(idx, 1)
  gsap.killTweensOf(cat.position)
  gsap.killTweensOf(cat.rotation)
  gsap.killTweensOf(cat.scale)
  splatterBurst({ scene: sceneRef, position: cat.position, colors: FERAL_BURST, count: 12 })
  sceneRef?.remove(cat)
  // Same decrement path as killPeasant: we already pulled the mesh from `list`,
  // so the GoonOwnedChange→sync this fires won't double-remove.
  season.adjustGoonsOwned(goonId, -1, 'turned feral')
  return pos
}

// Per-frame collision avoidance. Each non-evading peasant checks separation
// against the hero + every other peasant; if too close to anyone, it retargets
// away. Y-axis (hop) tweens are unaffected — only x/z tweens are replaced.
export function tickPeasants() {
  tickFieldCrew(performance.now())
  const all = []
  for (const list of meshes.values()) for (const p of list) if (!p.userData.resting && !p.userData.patient) all.push(p) // (resting citizens are walking in / asleep, patients are in the Hospital: no dodging, no school)

  // Walk OVER the rice rows: ease each citizen onto the mound surface when it crosses a row.
  for (const p of all) {
    if (p.userData.dying || p.userData.hopping || !p.userData.synty) continue
    const target = GROUND_Y + groundLift(p.position.x, p.position.z)
    p.position.y += (target - p.position.y) * 0.35
  }
  // Crew workers running the rows leave a wake of glints and the row they are on flashes (every WAKE_EVERY_MS while moving).
  if (fxRef) {
    const nowMs = performance.now()
    for (const p of all) {
      const u = p.userData
      if (!u.fieldJob || u.evading) continue
      if (nowMs - (u.lastWakeAt ?? 0) < WAKE_EVERY_MS) continue
      const moved = u.lastWakePos ? Math.hypot(p.position.x - u.lastWakePos.x, p.position.z - u.lastWakePos.z) : 1
      u.lastWakeAt = nowMs; u.lastWakePos = { x: p.position.x, z: p.position.z }
      if (moved < 0.02) continue
      fxRef.wake(p.position.x, p.position.y, p.position.z)
      const row = riceRef?.rowAt(p.position.x, p.position.z)
      if (row) riceRef.markHot(row.key, row.ri, 0.6)
    }
  }
  // The restless mark pulses (and spins slowly) so the eye finds it.
  const tp = performance.now() * 0.001
  for (const p of all) {
    const m = p.userData.restlessMark
    if (!m) continue
    m.scale.setScalar((m.userData.baseScale ?? 1) * (1 + 0.18 * Math.sin(tp * 7)))
    m.rotation.y = tp * 1.6
  }

  // School education — a citizen that wanders within range of a School
  // graduates once: gets a mortarboard + counts toward the educated bonus.
  if (schoolPositions.length) {
    for (const peasant of all) {
      if (peasant.userData.educated || peasant.userData.dying) continue
      for (const sp of schoolPositions) {
        if (Math.hypot(peasant.position.x - sp.x, peasant.position.z - sp.z) <= SchoolConfig.educateRadius) {
          if (season.educateCitizen()) {
            peasant.userData.educated = true
            addMortarboard(peasant)
            clearRestless(peasant) // schooled in time: it settles
          }
          break
        }
      }
    }
  }

  // Hospital healing — an injured citizen that wanders near a Hospital recovers.
  if (hospitalPositions.length) {
    for (const peasant of all) {
      if (!peasant.userData.injured || peasant.userData.dying) continue
      for (const hp of hospitalPositions) {
        if (Math.hypot(peasant.position.x - hp.x, peasant.position.z - hp.z) <= HospitalConfig.healRadius) {
          if (restoreHealth(peasant)) {
            season.healCitizen()
            splatterBurst({ // a little green "all better" sparkle
              scene: sceneRef, position: peasant.position,
              colors: [0x66ff99, 0xbfffd6, 0xffffff], count: 8,
            })
          }
          break
        }
      }
    }
  }

  // Devout citizens (Temple-blessed) mend any injured cat they brush past — the
  // mobile counterpart to the Hospital's fixed healing radius.
  for (const d of all) {
    if (!d.userData.devout || d.userData.dying) continue
    for (const p of all) {
      if (p === d || !p.userData.injured || p.userData.dying) continue
      if (Math.hypot(p.position.x - d.position.x, p.position.z - d.position.z) <= PositiveToolConfig.devoutHealRadius) {
        if (restoreHealth(p)) {
          season.healCitizen()
          splatterBurst({ scene: sceneRef, position: p.position, colors: [0x66ff99, 0xbfffd6, 0xffffff], count: 8 })
        }
      }
    }
  }

  for (const peasant of all) {
    if (peasant.userData.evading || peasant.userData.dying || peasant.userData.frozen) continue
    if (peasant.userData.fieldJob) continue // a row worker keeps to its row; the others step aside for it (otherwise a crowded paddy could keep it paused for ever)
    let pushX = 0
    let pushZ = 0
    let collided = false
    const check = (otherPos) => {
      const dx = peasant.position.x - otherPos.x
      const dz = peasant.position.z - otherPos.z
      const dist = Math.hypot(dx, dz)
      if (dist > 0.001 && dist < SEPARATION_DIST) {
        const weight = (SEPARATION_DIST - dist) / SEPARATION_DIST
        pushX += (dx / dist) * weight
        pushZ += (dz / dist) * weight
        collided = true
      }
    }
    if (heroRef) check(heroRef.position)
    for (const other of all) {
      if (other === peasant || other.userData.dying) continue
      check(other.position)
    }
    if (collided) pauseThenRedirect(peasant, pushX, pushZ)
  }
}

// A bump: stop where it happened and stand idle (the walk cycle drops to idle by itself once it stops moving), and only after
// CollisionConfig.pauseMs turn and walk away. While it waits it is "evading", so it isn't bumped again or re-targeted.
function pauseThenRedirect(peasant, dirX, dirZ) {
  const u = peasant.userData
  u.evading = true
  gsap.killTweensOf(peasant.position, 'x,z')
  u.pauseCall?.kill()
  u.pauseCall = gsap.delayedCall(CollisionConfig.pauseMs / 1000, () => {
    u.pauseCall = null
    if (u.dying || u.resting || !u.evading) return // something else took charge meanwhile (rest…)
    if (u.frozen) { u.evading = false; return }    // held for the positive tool: it will wander again when released
    if (u.fieldJob) { u.evading = false; resumeFieldJob(peasant); return } // a row worker just carries on with its row
    if (u.directed) { u.evading = false; u.directed.resume?.(); return }   // a directed citizen carries on with its errand
    retargetAway(peasant, dirX, dirZ)
  })
}

function retargetAway(peasant, dirX, dirZ) {
  peasant.userData.evading = true
  // Kill only x/z tweens — leave the hop (y) animation intact.
  gsap.killTweensOf(peasant.position, 'x,z')
  const len = Math.hypot(dirX, dirZ) || 1
  // Dodge away, but pull the target back until it's on the tile field so the
  // peasant never flees off the edge.
  let evadeDistance = 2 + Math.random() * 2
  let targetX = peasant.position.x + (dirX / len) * evadeDistance
  let targetZ = peasant.position.z + (dirZ / len) * evadeDistance
  // Back off until the dodge target is on-field AND not inside / behind a solid
  // object (so the panic-flee can't shove a peasant through a building).
  while (evadeDistance > 0.3 &&
         (!isInFieldWorld(targetX, targetZ) ||
          !pathClear(peasant.position.x, peasant.position.z, targetX, targetZ))) {
    evadeDistance *= 0.6
    targetX = peasant.position.x + (dirX / len) * evadeDistance
    targetZ = peasant.position.z + (dirZ / len) * evadeDistance
  }
  const dx = targetX - peasant.position.x
  const dz = targetZ - peasant.position.z
  peasant.rotation.y = Math.atan2(dx, dz)
  // Same duration formula + linear ease as wander() — the cat just turns and
  // walks the other way at its normal pace instead of snapping.
  const dist = Math.hypot(dx, dz)
  const duration = Math.max(1.5, dist * 1.4)
  gsap.to(peasant.position, {
    x: targetX,
    z: targetZ,
    duration,
    ease: 'none',
    onComplete: () => {
      peasant.userData.evading = false
      wander(peasant)
    },
  })
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v))
}

// Round-robin a hop across visible peasants of this goon type. If the picked
// peasant is mid-hop, advance until we find a free one or give up — keeps
// rapid auto-click rates from making the field look glitchy.
// A citizen's automatic click (goonManager): the old cats hopped; the villagers TEND — a green puff at the feet of the next citizen
// standing in a paddy (round-robin), the row under it flashes, and if the model carries a 'tend' clip (a Mixamo bend-and-plant)
// it plays once while the citizen stands still. Rate-limited per citizen (tendEveryMs) and overall (sparkles.js).
const TEND_EVERY_MS = 600
function pickAndHop(goonId) {
  const list = meshes.get(goonId)
  if (!list?.length) return
  const start = hopCursors.get(goonId) ?? 0
  const now = performance.now()
  for (let i = 0; i < list.length; i++) {
    const idx = (start + i) % list.length
    const p = list[idx]
    const u = p.userData
    if (u.hopping || u.frozen || u.dying || u.resting || u.inside || u.patient) continue
    if (!u.synty) { hopCursors.set(goonId, idx + 1); hop(p); return }
    if (now - (u.lastTendFx ?? 0) < TEND_EVERY_MS) continue
    const row = riceRef?.rowAt(p.position.x, p.position.z)
    if (!row && groundLift(p.position.x, p.position.z) <= 0.004) continue // not in a paddy: nothing to tend
    hopCursors.set(goonId, idx + 1)
    u.lastTendFx = now
    fxRef?.puff(p.position.x, p.position.y, p.position.z)
    if (row) riceRef.markHot(row.key, row.ri, 0.8)
    playTend(p)
    return
  }
}
// The optional 'tend' clip: play it once, standing still, then hand the character back to its automatic walk/idle.
const tendStats = { attempts: 0, played: 0, noSpot: 0, off: 0, busy: {} } // dev/test: why a tend did or did not kneel
function playTend(p) {
  const u = p.userData
  tendStats.attempts++
  if (!workAnims || !hasClip(p, 'tendpull')) { tendStats.off++; return }
  const busy = u.tending ? 'tending' : u.fieldJob ? 'fieldJob' : u.directed ? 'directed' : u.evading ? 'evading' : u.working ? 'working' : null
  if (busy) { tendStats.busy[busy] = (tendStats.busy[busy] ?? 0) + 1; return }
  const spot = riceRef?.nearestPlantSpot(p.position.x, p.position.z, WORK.reach)
  if (!spot) { tendStats.noSpot++; return } // no plant within reach: the puff marks the click, but nobody kneels over bare dirt
  tendStats.played++
  const dur = (clipDuration(p, 'tendpull') || 4.6) / WORK.pullTimeScale
  u.tending = true; u.tendingSince = performance.now() // (tendingSince: dev/test — proves no kneel outlives its clip)
  const tweens = gsap.getTweensOf(p.position); tweens.forEach((t) => t.pause())
  stepToPlant(p, spot) // beside the row, inside its blades, facing it
  riceRef.markHot(spot.key, spot.ri, 0.8)
  emit(Events.CitizenKneels, { goonId: u.goonId })
  const done = () => { if (!u.tending) return; u.tending = false; u.tendDone = null; backToAuto(p); tweens.forEach((t) => t.resume()) }
  u.tendDone = done // (a player's click or the row chain may cut this kneel short)
  playClip(p, 'tendpull', { once: true, fade: 0.15, timeScale: WORK.pullTimeScale, onDone: done })
  gsap.delayedCall(dur + 0.6, done) // (safety: never stuck)
}

// DIRECT A CITIZEN: the player clicked one — it jogs to the row the next click fills (or row k), kneels at a plant there and, if it
// pays, drops a coin: one notch for the season and DirectTendConfig.payMult of a click's value (seasonManager.registerTend). One
// paid errand per citizen every cooldownMs, one for the village every globalGapMs. The ROW CHAIN uses the same errand without pay:
// a filled row calls the nearest idle citizen within chainReach to kneel on it. Returns true when the citizen set off.
let lastDirectAt = -1e9
export function directCitizen(p, { k = null, pay = true } = {}) {
  const u = p?.userData; if (!u || !riceRef) return false
  if (u.patient || u.dying || u.resting || u.inside || u.fieldJob || u.directed || u.working || u.frozen || !hasClip(p, 'tendpull')) return false
  const now = performance.now()
  if (pay && (now - (u.directedAt ?? -1e9) < DirectTendConfig.cooldownMs || now - lastDirectAt < DirectTendConfig.globalGapMs)) return false
  if (u.tending) u.tendDone?.() // (its own kneel gives way to the errand)
  const row = k == null ? riceRef.currentRowCentre() : riceRef.rowCentre(k); if (!row) return false
  const spot = riceRef.nearestPlantSpot(row.x, row.z, 3); if (!spot) return false
  u.directed = { k: row.k, pay, spot, knelt: false }
  if (pay) { u.directedAt = now; lastDirectAt = now }
  u.pauseCall?.kill(); u.pauseCall = null; u.evading = false; u.restTl?.kill()
  const finish = () => { if (!u.directed) return; u.directed = null; backToAuto(p); if (!u.dying && !u.resting && !u.frozen) wander(p) }
  gsap.delayedCall(DirectTendConfig.giveUpMs / 1000, () => { if (u.directed && !u.directed.knelt) finish() }) // (never stuck: no kneel in time → back to roaming)
  const kneel = () => {
    if (!u.directed || u.directed.knelt) return
    u.directed.knelt = true
    p.rotation.y = spot.face
    riceRef.markHot(spot.key, spot.ri, 0.9)
    fxRef?.puff(p.position.x, p.position.y, p.position.z)
    const dur = (clipDuration(p, 'tendpull') || 4.6) / WORK.pullTimeScale
    playClip(p, 'tendpull', { once: true, fade: 0.2, timeScale: WORK.pullTimeScale, onDone: finish })
    gsap.delayedCall(dur * 0.45, () => { // the pull: the coin
      if (!u.directed?.pay) return
      const delta = season.registerTend({ goonId: u.goonId, payMult: DirectTendConfig.payMult })
      fxRef?.burst(p.position.x, p.position.y + 0.1, p.position.z, 6, [1.0, 0.86, 0.42])
      const sc = fxRef?.toScreen?.(p.position.x, p.position.y + 0.3, p.position.z); if (sc) emit(Events.CoinPopAt, { x: sc.x, y: sc.y, delta })
    })
    gsap.delayedCall(dur + 0.6, finish) // (safety: never stuck)
  }
  const walk = () => { // (a bump can interrupt the walk — it resumes from where it stopped)
    if (!u.directed || u.directed.knelt) return
    gsap.killTweensOf(p.position, 'x,z')
    const dx = spot.x - p.position.x, dz = spot.z - p.position.z, dist = Math.hypot(dx, dz)
    p.rotation.y = Math.atan2(dx, dz)
    if (dist < 0.05) kneel()
    else gsap.to(p.position, { x: spot.x, z: spot.z, duration: Math.max(0.2, dist * 1.4), ease: 'none', onComplete: kneel })
  }
  u.directed.resume = walk
  walk()
  return true
}
// The row chain: a row just filled — the nearest idle citizen within reach goes to kneel on it (for show).
function chainToRow(k) {
  const row = riceRef?.rowCentre(k); if (!row) return false
  let best = null, bd = DirectTendConfig.chainReach
  for (const list of meshes.values()) for (const p of list) {
    const u = p.userData
    if (!p.visible || u.patient || u.dying || u.resting || u.inside || u.fieldJob || u.directed || u.working || u.frozen) continue
    const d = Math.hypot(p.position.x - row.x, p.position.z - row.z); if (d < bd) { bd = d; best = p }
  }
  return best ? directCitizen(best, { k, pay: false }) : false
}

// A short step to the plant spot (scene/riceField.js nearestPlantSpot) and a turn to face the row, so the kneel-and-pull is over rice.
function stepToPlant(p, spot) {
  gsap.to(p.position, { x: spot.x, z: spot.z, duration: 0.25, ease: 'power1.out' })
  p.rotation.y = spot.face
}

function hop(peasant) {
  // The hop was the cats' income-tick cue. The Synty villagers walk and stand instead, so they don't bounce.
  if (peasant.userData.synty) return
  peasant.userData.hopping = true
  gsap
    .timeline({
      onComplete: () => {
        peasant.userData.hopping = false
      },
    })
    .to(peasant.position, { y: GROUND_Y + 0.3, duration: 0.12, ease: 'power2.out' })
    .to(peasant.position, { y: GROUND_Y, duration: 0.18, ease: 'bounce.out' })
}

function sync(goonId, owned) {
  const list = meshes.get(goonId)
  const target = Math.min(owned, VisualGoonCap)
  while (list.length < target) {
    const peasant = createPeasant(goonId)
    sceneRef.add(peasant)
    list.push(peasant)
    if (isResting()) hideInside(peasant) // bought (or loaded) during a rest: straight to bed
    else wander(peasant)
  }
  while (list.length > target) {
    const i = list.findIndex((p) => p.userData.patient && p.userData.inside) // a patient who died leaves from the Hospital, not from the field
    const removed = i >= 0 ? list.splice(i, 1)[0] : list.pop()
    gsap.killTweensOf(removed.position)
    removed.userData.restTl?.kill()
    sceneRef.remove(removed)
  }
}

function createPeasant(goonId) {
  // Citizens are Synty villagers: Peasant = the village woman, Scribe = the village man. Nobles stay cats.
  const kind = goonId === GoonTypes.PEASANT_CAT ? 'villager_f' : goonId === GoonTypes.SCRIBE_CAT ? 'villager_m' : null
  const cat = (syntyCharsReady() && kind && createSyntyChar(kind)) || Assets.get(AssetKeyByGoon[goonId]) || buildPlaceholder(goonId)
  cat.userData.goonId = goonId // so killPeasant() can decrement the right inventory
  { // spawn on open ground, not inside a building
    let sp = randomFieldPoint()
    for (let i = 0; i < 8 && isBlocked(sp.x, sp.z); i++) sp = randomFieldPoint()
    cat.position.set(sp.x, GROUND_Y, sp.z)
  }
  cat.scale.setScalar(scaleOf(cat))
  // Spawned mid-famine? Wear the current body scale so the new cat blends in.
  if (currentHungerState !== 'healthy') applyHungerVisual(cat, currentHungerState)
  applyHungerMark(cat, currentHungerStage)
  return cat
}

function buildPlaceholder(goonId) {
  const c = ColorByGoon[goonId] ?? ColorByGoon[GoonTypes.PEASANT_CAT]
  const cat = new THREE.Group()
  cat.userData.placeholder = true

  // ~30% bigger than the original peasant — visibility against the paddy
  // tiles + reads as "intimidating cat" instead of "background sprite".
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.5, 0.55, 0.4),
    new THREE.MeshLambertMaterial({ color: c.body }),
  )
  body.position.y = 0.3
  body.userData.role = 'body' // hunger-state scaler finds the body by role
  cat.add(body)

  const head = new THREE.Mesh(
    new THREE.BoxGeometry(0.42, 0.4, 0.4),
    new THREE.MeshLambertMaterial({ color: c.head }),
  )
  head.position.y = 0.8
  cat.add(head)

  // Sharper, taller ears to read as menacing.
  const earGeo = new THREE.BoxGeometry(0.12, 0.16, 0.12)
  const earMat = new THREE.MeshLambertMaterial({ color: c.ear })
  const earL = new THREE.Mesh(earGeo, earMat)
  earL.position.set(-0.13, 1.06, 0)
  const earR = new THREE.Mesh(earGeo, earMat)
  earR.position.set(0.13, 1.06, 0)
  cat.add(earL, earR)

  // Plain cat eyes (loyal peasants). Wild goons override this with a glowing
  // red MeshBasicMaterial inside wildGoons.js — that contrast is the visual
  // signal "this one's mine vs. that one's hostile".
  const eyeGeo = new THREE.BoxGeometry(0.06, 0.06, 0.05)
  const eyeMat = new THREE.MeshLambertMaterial({ color: EYE_COLOR })
  const eyeL = new THREE.Mesh(eyeGeo, eyeMat)
  eyeL.position.set(-0.09, 0.82, 0.22)
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat)
  eyeR.position.set(0.09, 0.82, 0.22)
  cat.add(eyeL, eyeR)

  return cat
}

function randomPaddyCoord() {
  const range = FIELD_HALF * 2 - 1
  return (Math.random() - 0.5) * range
}

// Is the straight segment (x0,z0)→(x1,z1) free of blocked cells? Sampled at a
// resolution finer than the collision grid so a peasant's tween never cuts
// through a building/wall. Used to pick only reachable wander targets.
function pathClear(x0, z0, x1, z1) {
  const dist = Math.hypot(x1 - x0, z1 - z0)
  const steps = Math.max(1, Math.ceil(dist / 0.25))
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    if (isBlocked(x0 + (x1 - x0) * t, z0 + (z1 - z0) * t)) return false
  }
  return true
}

// A random on-field point the peasant can walk to in a straight line without
// crossing a solid object. Falls back to any in-field point after a few tries.
function reachableFieldPoint(fromX, fromZ) {
  for (let i = 0; i < 8; i++) {
    const p = randomFieldPoint()
    if (!isBlocked(p.x, p.z) && pathClear(fromX, fromZ, p.x, p.z)) return p
  }
  return randomFieldPoint()
}

// FIELDS mode — a reachable spot at the citizen's NEAREST rice paddy (they
// migrate to whichever field is closest, then mill around it). Falls back to
// roaming if there are no paddies yet.
function nearestFarmPoint(fromX, fromZ) {
  if (!farmPositions.length) return reachableFieldPoint(fromX, fromZ)
  let best = farmPositions[0]
  let bestD = Infinity
  for (const f of farmPositions) {
    const d = Math.hypot(f.x - fromX, f.z - fromZ)
    if (d < bestD) { bestD = d; best = f }
  }
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2
    const r = Math.random() * 1.1 // mill around the paddy
    const x = best.x + Math.cos(a) * r
    const z = best.z + Math.sin(a) * r
    if (isInFieldWorld(x, z) && !isBlocked(x, z) && pathClear(fromX, fromZ, x, z)) return { x, z }
  }
  return best
}

// VILLAGE mode — a reachable spot clustered near the village center (workers
// pull back to guard the buildings instead of roaming the whole field).
function reachableVillagePoint(fromX, fromZ) {
  for (let i = 0; i < 8; i++) {
    const a = Math.random() * Math.PI * 2
    const r = Math.random() * 2.6 // within ~2.6 units of home/center
    const x = Math.cos(a) * r
    const z = Math.sin(a) * r
    if (isInFieldWorld(x, z) && !isBlocked(x, z) && pathClear(fromX, fromZ, x, z)) return { x, z }
  }
  return { x: 0, z: 0 }
}

// ── REST / WORK ──────────────────────────────────────────────────────────────────────────────────────────────────────
// REST: every citizen walks to the NEAREST building (any building, or the home) and fades out as it goes in. WORK: they step
// back out of the door and take up wandering again. Flags on userData: `resting` (heading in, or asleep), `inside` (asleep).
function restSpots() {
  const spots = getBuildings().map((b) => tileToWorld(b.tileIndex))
  const home = getHomeTileIndex()
  if (home != null) spots.push(tileToWorld(home))
  return spots
}
function nearestRestSpot(x, z) {
  let best = null
  let bestD = Infinity
  for (const s of restSpots()) {
    const d = Math.hypot(s.x - x, s.z - z)
    if (d < bestD) { bestD = d; best = s }
  }
  return best
}
// A citizen's own materials (the body, and a scribe's graduation cap) — never the shared overlay materials.
const ownMats = (cat) => [cat.userData.mat, cat.userData.cap?.material].filter(Boolean)
function fadeCitizen(cat, to, seconds) {
  for (const m of ownMats(cat)) {
    gsap.killTweensOf(m)
    if (!seconds) { m.opacity = to; m.transparent = to < 1; continue }
    m.transparent = true
    gsap.to(m, { opacity: to, duration: seconds, ease: to ? 'power1.out' : 'power1.in', onComplete: () => { if (to === 1) m.transparent = false } })
  }
}
// Walk to the building, start fading as the building is reached, and be gone by the time the citizen is inside it.
function retreat(p) {
  const u = p.userData
  if (u.inside || u.dying) return
  u.restTl?.kill()
  u.pauseCall?.kill(); u.pauseCall = null
  u.fieldJob = null // (a rest ends any row work)
  u.resting = true
  u.evading = false
  gsap.killTweensOf(p.position, 'x,z')
  const spot = nearestRestSpot(p.position.x, p.position.z)
  if (!spot) { hideInside(p); return }
  const dx = spot.x - p.position.x
  const dz = spot.z - p.position.z
  const dist = Math.hypot(dx, dz) || 1
  const enter = Math.min(RestConfig.enterDist, dist)
  const lead = dist - enter // walk up to the door…
  const fade = RestConfig.fadeMs / 1000
  p.rotation.y = Math.atan2(dx, dz)
  u.restTl = gsap.timeline({
    onComplete: () => { if (u.resting) { p.visible = false; u.inside = true } },
  })
    .to(p.position, { x: p.position.x + (dx / dist) * lead, z: p.position.z + (dz / dist) * lead, duration: Math.max(0.2, lead * RestConfig.walkSecPerUnit), ease: 'none' })
    .add(() => fadeCitizen(p, 0, fade)) // …then fade out as it steps through the door
    .to(p.position, { x: spot.x, z: spot.z, duration: fade, ease: 'none' }, '<')
}
// Already asleep (a citizen bought, or a game loaded, during a rest): straight into the nearest building, no walk.
function hideInside(p) {
  const u = p.userData
  if (u.dying) return
  u.restTl?.kill()
  u.pauseCall?.kill(); u.pauseCall = null
  u.fieldJob = null
  gsap.killTweensOf(p.position, 'x,z')
  u.resting = true
  u.inside = true
  u.evading = false
  const spot = nearestRestSpot(p.position.x, p.position.z)
  if (spot) { p.position.x = spot.x; p.position.z = spot.z }
  fadeCitizen(p, 0, 0)
  p.visible = false
}
// A free spot just outside a building's door.
function doorstep(x, z) {
  for (let i = 0; i < 12; i++) {
    const a = Math.random() * Math.PI * 2
    const r = 0.7 + Math.random() * 0.4
    const px = x + Math.cos(a) * r
    const pz = z + Math.sin(a) * r
    if (isInFieldWorld(px, pz) && !isBlocked(px, pz)) return { x: px, z: pz }
  }
  return { x, z }
}
// Back to work: step out of the door, fade in, and wander off to the fields (or the village, per the work mode).
function emerge(p, animate = true) {
  const u = p.userData
  if (u.dying) return
  u.restTl?.kill()
  const wasInside = u.inside
  u.resting = false
  u.inside = false
  p.visible = true
  if (wasInside) { const d = doorstep(p.position.x, p.position.z); p.position.x = d.x; p.position.z = d.z }
  fadeCitizen(p, 1, animate ? RestConfig.fadeMs / 1000 : 0)
  wander(p)
}
// ── THE HOSPITAL (src/game/hospital.js) ──────────────────────────────────────────────────────────────────────────────────
// An admitted citizen walks to the Hospital building (or home, as a sickbed) and fades inside: flags `patient` (admitted) and
// `inside` (arrived). It earns nothing there. Healed patients step back out of the door; a patient who died leaves from inside.
function hospitalSpot(x, z) {
  let best = null, bestD = Infinity
  for (const s of hospitalPositions) { const d = Math.hypot(s.x - x, s.z - z); if (d < bestD) { bestD = d; best = s } }
  if (best) return best
  const home = getHomeTileIndex()
  return home != null ? tileToWorld(home) : { x: 0, z: 0 }
}
function walkIntoHospital(p) {
  const u = p.userData
  if (u.dying || u.inside) return
  u.restTl?.kill()
  u.pauseCall?.kill(); u.pauseCall = null
  u.fieldJob = null
  u.evading = false
  u.patient = true
  clearRestless(p)
  gsap.killTweensOf(p.position, 'x,z')
  const spot = hospitalSpot(p.position.x, p.position.z)
  const dx = spot.x - p.position.x, dz = spot.z - p.position.z
  const dist = Math.hypot(dx, dz) || 1
  const enter = Math.min(RestConfig.enterDist, dist)
  const lead = dist - enter
  const fade = RestConfig.fadeMs / 1000
  p.rotation.y = Math.atan2(dx, dz)
  u.restTl = gsap.timeline({ onComplete: () => { if (u.patient) { p.visible = false; u.inside = true } } })
    .to(p.position, { x: p.position.x + (dx / dist) * lead, z: p.position.z + (dz / dist) * lead, duration: Math.max(0.2, lead * RestConfig.walkSecPerUnit * 1.6), ease: 'none' }) // (weak with hunger: slower)
    .add(() => fadeCitizen(p, 0, fade))
    .to(p.position, { x: spot.x, z: spot.z, duration: fade, ease: 'none' }, '<')
}
function hideInHospital(p) {
  const u = p.userData
  if (u.dying) return
  u.restTl?.kill()
  u.pauseCall?.kill(); u.pauseCall = null
  u.fieldJob = null
  gsap.killTweensOf(p.position, 'x,z')
  u.patient = true; u.inside = true; u.evading = false
  const spot = hospitalSpot(p.position.x, p.position.z)
  p.position.x = spot.x; p.position.z = spot.z
  fadeCitizen(p, 0, 0)
  p.visible = false
}
// One admission (Events.CitizenAdmitted): the nearest healthy, free citizen of that kind walks in.
function admitToHospital(goonId) {
  const list = meshes.get(goonId) ?? []
  const free = list.filter((p) => !p.userData.patient && !p.userData.dying && !p.userData.resting && !p.userData.inside)
  if (!free.length) { syncPatients(); return }
  const spot = hospitalSpot(0, 0)
  free.sort((a, b) => Math.hypot(a.position.x - spot.x, a.position.z - spot.z) - Math.hypot(b.position.x - spot.x, b.position.z - spot.z))
  const p = free[0]
  if (p.userData.frozen) p.userData.frozen = false
  walkIntoHospital(p)
}
function releasePatients() {
  for (const list of meshes.values()) for (const p of list) {
    if (!p.userData.patient) continue
    p.userData.patient = false
    p.userData.restTl?.kill()
    if (p.userData.inside) emerge(p, true)
    else { p.userData.resting = false; fadeCitizen(p, 1, 0); if (!isResting()) wander(p) }
  }
}
// Keep the visible patients in step with the Hospital's list (after a load, a kingdom switch, a death): hide extras, release surplus.
function syncPatients() {
  for (const [goonId, list] of meshes) {
    const want = hospital.count(goonId)
    const have = list.filter((p) => p.userData.patient)
    if (have.length < want) {
      const free = list.filter((p) => !p.userData.patient && !p.userData.dying)
      for (const p of free.slice(0, want - have.length)) hideInHospital(p)
    } else if (have.length > want) {
      for (const p of have.slice(0, have.length - want)) { p.userData.patient = false; p.userData.restTl?.kill(); if (p.userData.inside) emerge(p, true) }
    }
  }
}

function applyRest(animate) {
  const resting = isResting()
  for (const list of meshes.values()) {
    for (const p of list) {
      const u = p.userData
      if (u.dying || u.patient) continue // (patients stay in the Hospital through a rest)
      if (resting) {
        if (u.inside) continue
        if (animate) retreat(p)
        else hideInside(p)
      } else if (u.resting) emerge(p, animate)
    }
  }
}

// ── FIELD CREW ───────────────────────────────────────────────────────────────────────────────────────────────────────────
// While the player clicks the clicker rapidly, up to FieldWorkConfig.crew citizens (peasants first, scribes only if there are not
// enough peasants) work the rows of the nearest rice paddy at their normal walking pace — up and down their share of the rows,
// FieldWorkConfig.passes times over, then on to the next-nearest paddy — while everyone else keeps roaming. When the clicking
// stops they go back to roaming. A worker's state is `userData.fieldJob` = { slot, crew, worked, fieldId, lastId, legs, i }; each leg
// is a straight tween like a wander leg, so bumps, freezing and rest keep working as they always did (wander() resumes the job).
const WALKABLE = (x, z) => isInFieldWorld(x, z) && !isBlocked(x, z)
const WAKE_EVERY_MS = 160
// Field-work animations (Mixamo clips merged into the villager models — see scripts/capital/build_character.py MIXAMO_CLIPS):
//   'tendpull' — kneel and pull a plant, played once at a row's end (and when a citizen tends on its own click)
// Both are chosen at random per leg / row end so a crew looks busy rather than mechanical. workAnims = false turns them off (tests).
const WORK = { pullChance: 0.35, pullTimeScale: 1.6, reach: 1.0 } // reach: a plant within this many world units (≈4 m) is close enough to step to and pull
let workAnims = true
export function setWorkAnims(on) { workAnims = !!on }
const hasClip = (p, name) => !!p.userData.synty?.actions?.[name]
// Hand a character back to its automatic walk/idle after a game-driven clip.
function backToAuto(p) { const s = p.userData.synty; if (!s) return; s.mode = 'auto'; s.cur = null; s.play(s.walk ?? s.idle) }
const fieldClicks = createRapidClicks(FieldWorkConfig)
let lastCrewCheck = 0

function crewWorkers() {
  const out = []
  for (const list of meshes.values()) for (const p of list) if (p.userData.fieldJob) out.push(p)
  return out
}
function freeForRows(p) {
  const u = p.userData
  return !u.dying && !u.resting && !u.inside && !u.injured && !u.frozen && !u.fieldJob && !u.patient
}

function tickFieldCrew(now) {
  const crew = crewWorkers()
  if (!fieldClicks.rapid(now) || isResting()) { for (const p of crew) endFieldJob(p); return }
  if (now - lastCrewCheck < FieldWorkConfig.recruitEveryMs) return
  lastCrewCheck = now
  const fields = getPaddyFields(FieldWorkConfig.rowEndMarginM)
  if (!fields.length) { for (const p of crew) endFieldJob(p); return }
  const need = FieldWorkConfig.crew - crew.length
  if (need <= 0) return
  const candidates = []
  for (const [goonId, list] of meshes) {
    if (goonId !== GoonTypes.PEASANT_CAT && goonId !== GoonTypes.SCRIBE_CAT) continue // (nobles do not work the rows)
    for (const p of list) if (freeForRows(p)) candidates.push({ id: p, peasant: goonId === GoonTypes.PEASANT_CAT, x: p.position.x, z: p.position.z })
  }
  const picked = pickCrew(candidates, fields, need)
  const size = crew.length ? crew[0].userData.fieldJob.crew : picked.length // (rows are shared out among the crew that is actually there)
  const slots = new Set(crew.map((p) => p.userData.fieldJob.slot))
  for (const p of picked) {
    let slot = 0
    while (slots.has(slot)) slot += 1
    if (slot >= size) break
    slots.add(slot)
    startFieldJob(p, slot, size)
  }
}

function startFieldJob(p, slot, crew) {
  const u = p.userData
  u.pauseCall?.kill(); u.pauseCall = null
  u.evading = false
  gsap.killTweensOf(p.position, 'x,z')
  u.fieldJob = { slot, crew, worked: new Set(), fieldId: null, lastId: null, legs: [], i: 0 }
  planFieldJob(p)
}

// Pick the next paddy and plan the walk there and along this worker's rows.
function planFieldJob(p) {
  const job = p.userData.fieldJob
  if (!job) return
  const fields = getPaddyFields(FieldWorkConfig.rowEndMarginM)
  for (let tries = 0; tries < 4; tries += 1) {
    const pick = nextField(p.position, fields, job.worked, job.slot, job.crew, job.lastId)
    if (!pick) break
    if (pick.newRound) job.worked = new Set()
    const plan = planLegs(p.position, pick.field, job.slot, job.crew, FieldWorkConfig.passes, WALKABLE)
    if (plan) { job.fieldId = pick.field.id; job.legs = plan.legs; job.travel = plan.travel; job.i = 0; runFieldLeg(p); return }
    job.worked.add(pick.field.id) // can't get there: skip it this round
  }
  endFieldJob(p) // nothing to work (no paddy, or none reachable): back to roaming
}

function runFieldLeg(p) {
  const job = p.userData.fieldJob
  if (!job) return
  if (job.i >= job.legs.length) { job.worked.add(job.fieldId); job.lastId = job.fieldId; planFieldJob(p); return } // that paddy is done
  const t = job.legs[job.i]
  const dx = t.x - p.position.x
  const dz = t.z - p.position.z
  const dist = Math.hypot(dx, dz)
  if (dist < 0.03) { job.i += 1; runFieldLeg(p); return }
  p.rotation.y = Math.atan2(dx, dz)
  const u = p.userData
  // a row end (every second leg of the rows is the hop to the next row; the long legs are the rows themselves): sometimes kneel
  // and pull a plant before moving on — the character stands still for the clip
  const isRowLeg = job.i >= job.travel && dist > 0.6
  const spot = workAnims && isRowLeg && job.i > job.travel && hasClip(p, 'tendpull') && !u.working && Math.random() < WORK.pullChance ? riceRef?.nearestPlantSpot(p.position.x, p.position.z, WORK.reach) : null
  if (spot) { // (and only where there IS a plant to pull — beside the row, inside its blades, facing it)
    u.working = 'pull'
    stepToPlant(p, spot)
    riceRef.markHot(spot.key, spot.ri, 0.8)
    const dur = (clipDuration(p, 'tendpull') || 4.6) / WORK.pullTimeScale
    playClip(p, 'tendpull', { once: true, fade: 0.2, timeScale: WORK.pullTimeScale, onDone: () => { if (u.working === 'pull') { u.working = null; backToAuto(p); if (u.fieldJob === job) runFieldLeg(p) } } })
    gsap.delayedCall(dur + 0.6, () => { if (u.working === 'pull') { u.working = null; backToAuto(p); if (u.fieldJob === job) runFieldLeg(p) } }) // (safety: never stuck)
    return
  }
  gsap.to(p.position, { // the normal walking pace (the same formula as wander())
    x: t.x, z: t.z, duration: Math.max(0.2, dist * 1.4), ease: 'none',
    onComplete: () => { if (u.fieldJob === job) { job.i += 1; runFieldLeg(p) } },
  })
}

function resumeFieldJob(p) {
  gsap.killTweensOf(p.position, 'x,z')
  runFieldLeg(p)
}

function endFieldJob(p) {
  const u = p.userData
  if (!u.fieldJob) return
  u.fieldJob = null
  if (u.working) { u.working = null; backToAuto(p) }
  if (u.dying || u.resting || u.frozen) return
  gsap.killTweensOf(p.position, 'x,z')
  wander(p)
}

function wander(peasant) {
  if (peasant.userData.patient) return // in the Hospital
  if (isResting()) { if (!peasant.userData.inside) retreat(peasant); return } // on a rest nobody wanders — they head indoors
  if (peasant.userData.fieldJob) { resumeFieldJob(peasant); return } // a row worker carries on with its rows instead
  // Work mode steers where citizens go: FIELDS → migrate to the nearest rice
  // paddy and work it; VILLAGE → disperse near the center to guard the buildings.
  const village = season.getWorkMode() === WorkModes.VILLAGE
  const { x, z } = village
    ? reachableVillagePoint(peasant.position.x, peasant.position.z)
    : nearestFarmPoint(peasant.position.x, peasant.position.z)
  const dx = x - peasant.position.x
  const dz = z - peasant.position.z
  const dist = Math.hypot(dx, dz)
  const duration = Math.max(1.5, dist * 1.4)

  peasant.rotation.y = Math.atan2(dx, dz)

  gsap.to(peasant.position, {
    x,
    z,
    duration,
    ease: 'none',
    onComplete: () => wander(peasant),
  })
}

// PHASE 3: per-peasant pathfinding around buildings.
